import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { Column, Row, SelectBox } from '@lux/components';
import { noop, remCalc } from '@lux/helpers';

import {
  BodySecondary,
  FootnoteSecondary,
  H3,
  HR,
  SubtitlePrimary,
  SubtitleSecondary,
  UnorderedList
} from '../Base';
import LabelTag from '../LabelTag';
import Ribbon from '../Ribbon';
import Spacing from '../Spacing';
import Tag, { TagColors } from '../Tag';

import formatPrice from '../../helpers/formatPrice';
import unlimitedTalktimeSms from '../../helpers/unlimited-talktime-sms';
import isTagged from '../../helpers/isTagged';
import { planTagsConfig } from '../../config/plan-group-config';

const StyledCard = styled(SelectBox)`
  width: 100%;
  padding: ${p => p.theme.spacing(2)};
  flex-direction: column;
  opacity: 1;
  border: none;
  box-shadow: 0 0 0 1px ${p => p.theme.colours.grey_400};

  ${p =>
    p.disabled &&
    `
    box-shadow: none;
    background: ${p.theme.colours.grey_200};
    ${H3}, ${Ribbon}, > * {
      color: ${p.theme.colours.grey_500};
    }
  `};

  ${p => p.theme.media.md`
    padding: ${p.theme.spacing(3)};
  `};

  &:first-of-type {
    margin-top: 0;
  }

  &:hover {
    border: none;

    ${p =>
      !p.active &&
      !p.disabled &&
      css`
        box-shadow: 0 0 0 1px ${p.theme.colours.grey_600};
      `};

    ${p =>
      p.disabled &&
      `
      box-shadow: none;
    `};
  }

  ${p =>
    p.active &&
    css`
      box-shadow: 0 0 0 2px ${p.theme.colours.link};
      cursor: default;
    `};
`;

const StyledDescription = styled.div`
  font-family: ${p => p.theme.fonts.primary};
  font-weight: normal;
  font-size: ${remCalc(14)};
  color: ${p =>
    p.disabled ? p.theme.colours.grey_500 : p.theme.colours.grey_600};
  line-height: 1.5;
  letter-spacing: ${remCalc(0.25)};
  margin: 0;
`;

const StyledFootnoteSecondary = styled(FootnoteSecondary)`
  color: ${p =>
    p.disabled ? p.theme.colours.grey_500 : p.theme.colours.grey_600};
`;

const StyledOriginalPrice = styled.span`
  text-decoration: line-through;
`;

const StyledSavingsPercentage = styled.span`
  color: ${p =>
    p.disabled ? p.theme.colours.grey_500 : p.theme.colours.grey_900};
  text-transform: none;
`;

const StyledRebate = styled(FootnoteSecondary)`
  line-height: normal;
  margin-bottom: -1px;
`;

const StyledDataTitle = styled.span`
  margin-right: 8px;
  position: relative;
  bottom: 2px;
`;

const StyledTag = styled.span`
  line-height: 0em;
`;

const StyledH3 = styled(H3)`
  display: flex;
  align-items: center;
`;

const PlanCard = ({
  name,
  active,
  ribbon,
  indicativePrice,
  originalPrice,
  originalPriceText,
  phonePrice,
  rebate,
  data,
  talktime,
  sms,
  planDescSubLiner,
  onPlanClick,
  children,
  size,
  disabled,
  tags
}) => {
  /**
   * Format the plan data to a standardised format
   *
   * @param {Number} quantity
   * @param {String} unit
   * @returns {string}
   */
  const formatPlanData = (quantity, unit) => {
    return `${quantity}${unit}`.replace(/\s/, '');
  };

  const { available } = size;

  const speedTag = isTagged(tags, planTagsConfig['5g-speed'].tagName);

  /**
   * Return tag based on plan speed
   * @param {*} speedTag
   */
  const renderTags = speedTag => {
    return speedTag ? (
      <Tag
        color={TagColors.blue}
        data-testid="networkspeed"
        css={{ marginTop: -10 }}
      >
        5G speed
      </Tag>
    ) : (
      <Tag
        color={TagColors.grey}
        data-testid="networkspeed"
        css={{ marginTop: -10 }}
      >
        4G speed
      </Tag>
    );
  };

  return (
    <StyledCard
      active={active && available}
      onClick={onPlanClick}
      disabled={disabled}
    >
      <Ribbon data-testid="plan-ribbon" disabled={disabled}>
        {ribbon}
      </Ribbon>
      <Row originalPrice={originalPrice} data-testid="planSelectBox">
        <Column>
          <Spacing bottom={1} bottomMd={2}>
            <SubtitleSecondary data-testid="plan-name" disabled={disabled}>
              {name}
            </SubtitleSecondary>
          </Spacing>
          <StyledH3>
            <StyledDataTitle data-testid="plan-data" disabled={disabled}>
              {formatPlanData(data.quantity, data.unit)}
            </StyledDataTitle>
            {tags && <StyledTag>{renderTags(speedTag)}</StyledTag>}
          </StyledH3>
        </Column>
      </Row>
      <Row>
        <Column sm={12}>
          <Spacing bottom={2} bottomMd={3}>
            <StyledDescription
              data-testid="plan-description"
              disabled={disabled}
            >
              {unlimitedTalktimeSms({ talktime, sms })}
              <UnorderedList
                dangerouslySetInnerHTML={{ __html: planDescSubLiner }}
              />
            </StyledDescription>
          </Spacing>
        </Column>
      </Row>
      <Spacing bottom={2} bottomMd={3}>
        <HR />
      </Spacing>
      <Row>
        <Column sm={6}>
          <BodySecondary>Plan</BodySecondary>
          <SubtitlePrimary data-testid="indicative-price">
            {`${formatPrice(indicativePrice)}/mth`}
          </SubtitlePrimary>
          {originalPrice && (
            <StyledFootnoteSecondary data-testid="original-price">
              U.P:{' '}
              <StyledOriginalPrice
                data-testid={`price-slash-${Boolean(originalPrice)}`}
              >{`${formatPrice(originalPrice)}`}</StyledOriginalPrice>{' '}
              {originalPriceText && (
                <StyledSavingsPercentage data-testid="original-price-text">
                  {originalPriceText}
                </StyledSavingsPercentage>
              )}
            </StyledFootnoteSecondary>
          )}
          {rebate && (
            <LabelTag>
              <StyledRebate>{rebate}</StyledRebate>
            </LabelTag>
          )}
        </Column>
        <Column sm={6}>
          <BodySecondary>Item</BodySecondary>
          <SubtitlePrimary>{phonePrice}</SubtitlePrimary>
        </Column>
      </Row>
      {children}
    </StyledCard>
  );
};

PlanCard.defaultProps = {
  id: '',
  name: '',
  active: false,
  ribbon: '',
  indicativePrice: '',
  originalPrice: '',
  originalPriceText: '',
  rebate: '',
  data: {},
  talktime: {},
  sms: {},
  size: {},
  onSizeChange: noop,
  onPlanClick: noop
};

const AllocationShape = PropTypes.shape({
  unit: PropTypes.string,
  quantity: PropTypes.string
});

PlanCard.propTypes = {
  /** Plan ID */
  id: PropTypes.string,
  /** Name of Plan */
  name: PropTypes.string,
  /** Active state of plan card */
  active: PropTypes.bool,
  /** Ribbon text */
  ribbon: PropTypes.string,
  /** Plan monthly charges after discount or reduction or other modifications */
  indicativePrice: PropTypes.string,
  /** Plan monthly charges */
  originalPrice: PropTypes.string,
  /** Description of the original price */
  originalPriceText: PropTypes.string,
  /** Sets the price of the phone */
  phonePrice: PropTypes.string,
  /** Bill rebate for the plan */
  rebate: PropTypes.string,
  /** Data allocation */
  data: AllocationShape,
  /** Talktime allocation */
  talktime: AllocationShape,
  /** Sms allocation */
  sms: AllocationShape,
  /** List of sizes to render */
  size: PropTypes.shape({
    active: PropTypes.bool,
    available: PropTypes.bool,
    name: PropTypes.string,
    planId: PropTypes.string,
    price: PropTypes.number
  }),
  /** HTML text for additional plan description */
  planDescSubLiner: PropTypes.node,
  /** Event handler for size click */
  onSizeChange: PropTypes.func,
  /** Event handler for plan click */
  onPlanClick: PropTypes.func
};

export default PlanCard;
